import { createPortal } from "react-dom";
import React, { type CSSProperties, type ReactNode } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { cx } from "./fieldContext";
import { useUiLanguage, useUiText } from "./uiText";
import { KEY_ENTER, KEY_ESCAPE, KEY_SPACE } from "./keyboardKeys";
const DAYS_PER_WEEK = 7;
const CALENDAR_WEEKS = 6;
const REFERENCE_SUNDAY_YEAR = 2024;
const REFERENCE_SUNDAY_DATE = 7;
const SUNDAY_FIRST_LANGUAGE = "en";

/*
 * Medien- und Kalender-Bausteine des Kits (Plan 2026-10-13-antd-ausbau, Stufe 4): Calendar (Monatsansicht),
 * Carousel, Image mit Vorschau. Schnittstelle wie Antd im genutzten Umfang.
 */

// ---------------------------------------------------------------- Calendar

export type CalendarCellInfo = { type: "date" | "month"; originNode: ReactNode; today: Dayjs };

export interface CalendarProps {
  value?: Dayjs;
  defaultValue?: Dayjs;
  onSelect?: (date: Dayjs, info: { source: "date" }) => void;
  onChange?: (date: Dayjs) => void;
  onPanelChange?: (date: Dayjs, mode: "month") => void;
  cellRender?: (current: Dayjs, info: CalendarCellInfo) => ReactNode;
  /** `null` blendet den Kopf aus (Seite führt den Monat selbst). */
  headerRender?: ((config: { value: Dayjs; onChange: (date: Dayjs) => void }) => ReactNode) | null;
  /** Große Monatsansicht (Standard) oder kompakte Ansicht. */
  fullscreen?: boolean;
  disabledDate?: (date: Dayjs) => boolean;
  className?: string;
  style?: CSSProperties;
}

function weekdayLabels(lang: string, firstDay: number): string[] {
  // display-format: Wochentagsnamen der Kalenderoberfläche folgen der UI-Sprache.
  const formatter = new Intl.DateTimeFormat(lang, { weekday: "short" });
  // 2024-01-07 war ein Sonntag.
  return Array.from({ length: DAYS_PER_WEEK }, (_, i) => formatter.format(new Date(REFERENCE_SUNDAY_YEAR, 0, REFERENCE_SUNDAY_DATE + ((firstDay + i) % DAYS_PER_WEEK))));
}

/** Monatskalender (Ersatz für Antd `Calendar`); Tageszellen über `cellRender`, Klick wählt den Tag. */
export function Calendar({ value, defaultValue, onSelect, onChange, onPanelChange, cellRender, headerRender, fullscreen = true, disabledDate, className, style }: CalendarProps) {
  const lang = useUiLanguage();
  const { text } = useUiText();
  const [inner, setInner] = React.useState<Dayjs>(() => value ?? defaultValue ?? dayjs());
  const current = value ?? inner;
  const firstDay = lang === SUNDAY_FIRST_LANGUAGE ? 0 : 1;
  const today = dayjs();
  const monthStart = current.startOf("month");
  const offset = (monthStart.day() - firstDay + DAYS_PER_WEEK) % DAYS_PER_WEEK;
  const gridStart = monthStart.subtract(offset, "day");
  const weeks = Array.from({ length: CALENDAR_WEEKS }, (_, w) => Array.from({ length: DAYS_PER_WEEK }, (_, d) => gridStart.add(w * DAYS_PER_WEEK + d, "day")));

  const change = (next: Dayjs, panel: boolean) => {
    if (value === undefined) setInner(next);
    onChange?.(next);
    if (panel || !next.isSame(current, "month")) onPanelChange?.(next, "month");
  };
  // display-format: Monatsname der Kalenderoberfläche folgt der UI-Sprache.
  const monthLabel = new Intl.DateTimeFormat(lang, { month: "long", year: "numeric" }).format(current.toDate());

  return (
    <div className={cx("ck-calendar", fullscreen ? "ck-calendar--full" : "ck-calendar--mini", className)} style={style}>
      {headerRender === null ? null : headerRender ? headerRender({ value: current, onChange: (next) => change(next, true) }) : (
        <div className="ck-calendar-header">
          <button type="button" className="ck-icon-button" aria-label={text("Previous page")} onClick={() => change(current.subtract(1, "month"), true)}><LeftOutlined aria-hidden /></button>
          <span className="ck-calendar-month">{monthLabel}</span>
          <button type="button" className="ck-icon-button" aria-label={text("Next page")} onClick={() => change(current.add(1, "month"), true)}><RightOutlined aria-hidden /></button>
        </div>
      )}
      <table className="ck-calendar-table">
        <thead>
          <tr>{weekdayLabels(lang, firstDay).map((label) => <th key={label} scope="col">{label}</th>)}</tr>
        </thead>
        <tbody>
          {weeks.map((week, w) => (
            <tr key={w}>
              {week.map((day) => {
                const inMonth = day.isSame(current, "month");
                const disabled = disabledDate?.(day) ?? false;
                const selected = day.isSame(current, "day");
                const isToday = day.isSame(today, "day");
                const originNode = <div className="ck-calendar-date-value">{day.date()}</div>;
                const content = cellRender ? cellRender(day, { type: "date", originNode, today }) : null;
                return (
                  <td
                    key={day.valueOf()}
                    className={cx("ck-calendar-cell", !inMonth && "is-outside", selected && "is-selected", isToday && "is-today", disabled && "is-disabled")}
                    aria-selected={selected}
                  >
                    <div
                      className="ck-calendar-date"
                      role="button"
                      tabIndex={disabled ? -1 : 0}
                      aria-disabled={disabled || undefined}
                      aria-label={day.format("YYYY-MM-DD")}
                      onClick={disabled ? undefined : () => { change(day, false); onSelect?.(day, { source: "date" }); }}
                      onKeyDown={(event) => {
                        if (disabled || (event.key !== KEY_ENTER && event.key !== KEY_SPACE)) return;
                        event.preventDefault();
                        change(day, false);
                        onSelect?.(day, { source: "date" });
                      }}
                    >
                      {originNode}
                      {fullscreen ? <div className="ck-calendar-date-content">{content}</div> : null}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- Carousel

export interface CarouselRef {
  next: () => void;
  prev: () => void;
  goTo: (slide: number, dontAnimate?: boolean) => void;
}

export interface CarouselProps {
  children?: ReactNode;
  dots?: boolean;
  afterChange?: (current: number) => void;
  beforeChange?: (current: number, next: number) => void;
  initialSlide?: number;
  className?: string;
  style?: CSSProperties;
}

/** Folien mit Punkten (Ersatz für Antd `Carousel`); steuerbar über `ref` (`next`, `prev`, `goTo`). */
export const Carousel = React.forwardRef<CarouselRef, CarouselProps>(function Carousel({ children, dots = true, afterChange, beforeChange, initialSlide = 0, className, style }, ref) {
  const slides = React.Children.toArray(children);
  const [index, setIndex] = React.useState(Math.min(initialSlide, Math.max(0, slides.length - 1)));
  const goTo = React.useCallback((target: number) => {
    const next = ((target % slides.length) + slides.length) % slides.length;
    if (next === index) return;
    beforeChange?.(index, next);
    setIndex(next);
    afterChange?.(next);
  }, [afterChange, beforeChange, index, slides.length]);
  React.useImperativeHandle(ref, () => ({ next: () => goTo(index + 1), prev: () => goTo(index - 1), goTo }), [goTo, index]);
  return (
    <div className={cx("ck-carousel", className)} style={style} aria-roledescription="carousel">
      <div className="ck-carousel-track" style={{ transform: `translateX(-${index * 100}%)` }}>
        {slides.map((slide, i) => (
          <div key={i} className="ck-carousel-slide" aria-hidden={i !== index} inert={i !== index ? true : undefined}>{slide}</div>
        ))}
      </div>
      {dots && slides.length > 1 ? (
        <div className="ck-carousel-dots" role="tablist">
          {slides.map((_, i) => (
            <button key={i} type="button" role="tab" aria-selected={i === index} aria-label={String(i + 1)} className={cx("ck-carousel-dot", i === index && "is-active")} onClick={() => goTo(i)} />
          ))}
        </div>
      ) : null}
    </div>
  );
});

// ---------------------------------------------------------------- Image

type ImagePreviewConfig = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Antd-Optionen ohne Wirkung in der Kit-Vorschau. */
  mask?: unknown;
  movable?: boolean;
  actionsRender?: unknown;
  src?: string;
};

export interface ImageProps {
  src?: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  style?: CSSProperties;
  wrapperStyle?: CSSProperties;
  className?: string;
  rootClassName?: string;
  preview?: boolean | ImagePreviewConfig;
  onClick?: React.MouseEventHandler<HTMLImageElement>;
  fallback?: string;
}

function ImagePreview({ src, alt, onClose }: { src: string; alt?: string; onClose: () => void }) {
  const { text } = useUiText();
  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === KEY_ESCAPE) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Am Dokument statt in der Karte: Karten mit Transform (Einblenden, Anheben) würden `position: fixed` sonst auf
  // die Kartengröße begrenzen. Jeder Klick schließt (auch aufs Bild), ebenso Escape.
  return createPortal(
    <div
      className="ck-image-preview"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={(event) => { event.stopPropagation(); return onClose(); }}
    >
      <button type="button" className="ck-image-preview-close" aria-label={text("Close")} onClick={onClose}><CloseOutlined aria-hidden /></button>
      <img src={src} alt={alt} />
    </div>,
    document.body,
  );
}

/** Bild mit Vollbildvorschau (Ersatz für Antd `Image`); Escape oder ein Klick schließt. */
function ImageBase({ src, alt, width, height, style, wrapperStyle, className, rootClassName, preview = true, onClick, fallback }: ImageProps) {
  const config = typeof preview === "object" ? preview : undefined;
  const [innerOpen, setInnerOpen] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const open = config?.open ?? innerOpen;
  const setOpen = (next: boolean) => {
    if (config?.open === undefined) setInnerOpen(next);
    config?.onOpenChange?.(next);
  };
  const enabled = preview !== false;
  const shown = failed && fallback ? fallback : src;
  return (
    <span className={cx("ck-image", rootClassName)} style={wrapperStyle}>
      <img
        src={shown}
        alt={alt}
        width={width}
        height={height}
        className={cx("ck-image-img", enabled && "is-previewable", className)}
        style={style}
        onError={() => setFailed(true)}
        onClick={(event) => { onClick?.(event); if (enabled && !event.defaultPrevented) setOpen(true); }}
      />
      {enabled && open && shown ? <ImagePreview src={config?.src ?? shown} alt={alt} onClose={() => setOpen(false)} /> : null}
    </span>
  );
}

function ImagePreviewGroup({ children }: { children?: ReactNode; preview?: unknown }) {
  return <>{children}</>;
}

export const Image = Object.assign(ImageBase, { PreviewGroup: ImagePreviewGroup });
